import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import CircleBg from "@/components/atomics/circle-bg";
import Star from "@/components/atomics/star";
import {
  LeaderboardItemType,
  LeaderboardMetric,
  MenuType,
} from "@/types/leader-board";
import {
  LEADERBOARD_CONSTANTS,
  METRIC_CONFIG,
  ROUTE_PATHS,
} from "@/constants/leaderboard";
import TruncateAddress from "@/components/utils/truncateAddress";

interface UserLeaderBoardInfoProps {
  className?: string;
  userInfo: LeaderboardItemType;
  selectedMenu: MenuType;
  metric: LeaderboardMetric;
  onClickRankBoost?: () => void;
  circleColor?: string;
  myrank?: boolean;
}

const AVATAR_SIZE = 66;
const ICON_SIZE = 34;
const CORNER_SIZE = "5%";
const DEFAULT_CIRCLE_COLOR = "#0099FE";

export default function UserLeaderBoardInfo({
  className = "",
  userInfo,
  selectedMenu,
  metric,
  onClickRankBoost,
  circleColor = DEFAULT_CIRCLE_COLOR,
  myrank = false,
}: UserLeaderBoardInfoProps) {
  const navigate = useNavigate();

  const formatValue = useCallback(
    (value: number): string => {
      const numeric = value || 0;

      if (metric === "volume") {
        return numeric.toFixed(2);
      }

      return Math.floor(numeric).toString();
    },
    [metric]
  );

  const handleAvatarClick = useCallback(() => {
    const routePath = ROUTE_PATHS[selectedMenu]?.(userInfo.address);
    if (routePath) {
      navigate(routePath);
    }
  }, [navigate, selectedMenu, userInfo.address]);

  const renderAvatar = () => {
    if (selectedMenu === "users") {
      return (
        <div
          className={`relative w-[66px] h-[66px]${onClickRankBoost ? " cursor-pointer" : ""}`}
          onClick={handleAvatarClick}
        >
          <CircleBg
            className="absolute z-[2] w-[66px] h-[66px] top-[50%] translate-y-[-50%] translate-x-[-50%] left-[50%] rounded-full"
            color={circleColor}
          />
          <div className="absolute top-[50%] translate-y-[-50%] left-[50%] translate-x-[-50%] w-[60px] h-[62px]">
            <img
              src={userInfo.avatar || LEADERBOARD_CONSTANTS.DEFAULT_AVATAR}
              alt={`${userInfo.name} avatar`}
              className="rounded-full w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      );
    } else {
      return (
        <div
          className="relative w-[66px] h-[66px] cursor-pointer"
          onClick={handleAvatarClick}
        >
          <img
            src={userInfo.avatar || LEADERBOARD_CONSTANTS.DEFAULT_AVATAR}
            alt={`${userInfo.name} collection`}
            className="w-full aspect-square object-cover"
            loading="lazy"
          />
          {Array.from({ length: 4 }, (_, index) => (
            <div
              key={index}
              className={`absolute w-[${CORNER_SIZE}] aspect-square ${
                index === 0
                  ? "top-0 left-0"
                  : index === 1
                    ? "top-0 right-0"
                    : index === 2
                      ? "bottom-0 left-0"
                      : "bottom-0 right-0"
              }`}
              style={{ backgroundColor: circleColor }}
            />
          ))}
        </div>
      );
    }

    return null;
  };

  const renderMetricIcon = () => {
    const colorMetric = myrank ? "all_stars" : metric;
    const config = METRIC_CONFIG[colorMetric];
    if (!config) return null;

    switch (metric) {
      case "all_stars":
        return (
          <Star
            className={`w-[${ICON_SIZE}px] h-[${ICON_SIZE}px] ${config.colorClass}`}
          />
        );
      case "volume":
        return (
          <span
            className={`text-[20px] ${config.colorClass} uppercase font-bold order-2`}
          >
            TON
          </span>
        );
      case "level":
        return (
          <span
            className={`text-[20px] ${config.colorClass} uppercase font-bold order-2`}
          >
            LVL
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={`bg-primary-blue px-5 py-2 w-full relative flex items-center justify-between ${className}`}
    >
      <div className="flex items-center gap-[15px]">
        {renderAvatar()}
        {myrank ? (
          <div className="flex flex-col leading-[1.2]">
            <span className="text-[20px] uppercase font-medium">My Rank:</span>
            <span className="text-[20px] font-semibold">
              {userInfo.rank || "No rank"}
            </span>
            <div className="flex">
              {selectedMenu === "nft" ? (
                <div className="max-w-[105px] text-[20px] font-medium truncate">
                  {userInfo.name}
                </div>
              ) : (
                <TruncateAddress
                  address={userInfo.name}
                  className="text-[20px] font-medium  max-w-[105px]"
                />
              )}
              {selectedMenu === "nft" &&
                userInfo.indexInCollection !== undefined && (
                  <span
                    className="text-[20px] font-medium"
                    title={userInfo.indexInCollection?.toString()}
                  >
                    #{userInfo.indexInCollection?.toString()}
                  </span>
                )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col leading-[1.2]">
            <div className="flex">
              <TruncateAddress
                address={userInfo.name}
                className="text-[20px] font-medium  max-w-[155px]"
              />
              {selectedMenu === "nft" &&
                userInfo.indexInCollection !== undefined && (
                  <span
                    className="text-[20px] font-medium"
                    title={userInfo.indexInCollection?.toString()}
                  >
                    ...#{userInfo.indexInCollection?.toString()}
                  </span>
                )}
            </div>
            <span className="text-[20px] uppercase font-medium">
              Rank: {userInfo.rank || "No rank"}
            </span>
          </div>
        )}
      </div>

      <div
        className={
          onClickRankBoost
            ? "flex flex-col items-center cursor-pointer hover:opacity-80 transition-opacity leading-[1.2]"
            : "flex flex-col items-center leading-[1.2] select-none"
        }
        onClick={onClickRankBoost}
      >
        <div className="flex w-[30px] justify-center items-center gap-[10px]">
          {renderMetricIcon()}
        </div>
        <span
          className={`text-[20px] ${METRIC_CONFIG[myrank ? "all_stars" : metric]?.colorClass || "text-primary-yellow"} font-medium`}
        >
          {formatValue(userInfo.value)}
        </span>
      </div>
    </div>
  );
}
